import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';
import classNames from 'classnames';

import Editor from './Editor';
import { Format, Modes } from './types';

interface IProps {
  fieldName: string;
  placeholderKey: string;
  labelKey?: string;
  errorKey?: string;
  validationType?: 'change' | 'submit';
}

const useStyles = makeStyles(theme => ({
  editor: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 4,
    '@global .public-DraftEditor-content': {
      minHeight: 300,
      fontSize: theme.typography.pxToRem(14),
    },
    '@global .public-DraftEditorPlaceholder-root': {
      top: 0,
      transform: 'translateY(0%)',
      fontSize: theme.typography.pxToRem(14),
    },
  },
  label: {
    marginTop: theme.spacing(2),
  },
  error: {
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

const HtmlEditor: FC<IProps> = ({
  fieldName,
  labelKey,
  placeholderKey,
  errorKey,
  validationType,
}) => {
  const classes = useStyles();
  const { errors, touched, setFieldValue, values } = useFormikContext();
  const { t } = useTranslation();

  let hasError = !!getIn(errors, fieldName);
  if (validationType === 'submit') {
    hasError = !!getIn(touched, fieldName) && !!getIn(errors, fieldName);
  }

  const errorText = !!errorKey ? t(errorKey) : getIn(errors, fieldName);
  const editorClasses = classNames(classes.editor, {
    [classes.error]: hasError,
    [classes.label]: !!labelKey,
  });

  return (
    <FormControl error={hasError} fullWidth>
      {!!labelKey && <InputLabel shrink>{t(labelKey)}</InputLabel>}
      <div className={editorClasses}>
        <Editor
          text={values[fieldName]}
          format={Format.html}
          placeholder={t(placeholderKey)}
          mode={Modes.edit}
          onChange={value => setFieldValue(fieldName, value)}
        />
      </div>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

HtmlEditor.defaultProps = {
  validationType: 'submit',
};

export default HtmlEditor;
